import { ReloadOutlined } from "@ant-design/icons";
import {
  Cancellation,
  Tenant,
  useAuthFetch,
  useHasRole,
} from "@p0security/frontend-core";
import * as firestoreProvider from "@p0security/frontend-core/react";
import { ErrorDisplay } from "@p0security/frontend-core/react";
import {
  PagerdutyIconUrl,
  PagerdutySharedConfig as PagerdutyIntegration,
} from "@p0security/integrations-pagerduty-shared";
import {
  Alert,
  Button,
  Select,
  Space,
  Spacing,
  Spin,
  Typography,
} from "@p0security/ui";
import { GraphTooltip } from "components/GraphTooltip";
import * as firestore from "firebase/firestore";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { OAuth2IntegrationCard } from "../IntegrationCard";

const PagerdutyPreamble: React.FC = () => (
  <>
    <Typography.Paragraph>
      Connect PagerDuty to automatically approve access requests for users
      currently on-call, and allow requestors to escalate requests to on-call
      users.
    </Typography.Paragraph>
    <Typography.Text strong>Prerequisites:</Typography.Text>
    <ul className="preamble-list">
      <li>
        <code>Admin</code> access in PagerDuty
      </li>
      <li>One or more escalation policies configured</li>
    </ul>
    <Spacing asChild mt={16} mb={16}>
      <Alert
        type="warning"
        showIcon
        message="Security note: Any PagerDuty member who can schedule an override for themselves on an enabled policy can gain infrastructure access without further approval."
      />
    </Spacing>
  </>
);

type PagerdutyPolicy = {
  name: string;
  id: string;
};

// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
// To test, you probably need to comment out `<React.StrictMode>`
// in `frontend/src/index.tsx`
// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

/** Selects schedules that cause auto-approval, and syncs these to Firestore */
const PagerdutyScheduleSelect: React.FC<{
  integration: firestoreProvider.KnownFirestoreDoc<PagerdutyIntegration>;
}> = ({ integration }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<any>();
  const authFetch = useAuthFetch(setError);
  const canEdit = useHasRole("owner");

  const onRefresh = useCallback(
    async (cancellation?: Cancellation) => {
      if (!canEdit) return;
      setIsRefreshing(true);
      try {
        await authFetch("integrations/pagerduty/refresh", {
          method: "POST",
          cancellation,
        });
      } finally {
        setIsRefreshing(false);
      }
    },
    [authFetch, canEdit]
  );

  const { docs: policies } =
    firestoreProvider.useFirestoreCollection<PagerdutyPolicy>(
      `integrations/pagerduty/escalation_policies`,
      { live: true, tenantAware: true }
    );
  const options = useMemo(
    () =>
      policies?.map((p) => (
        <Select.Option key={p.data.id} label={p.data.name}>
          {p.data.name}
        </Select.Option>
      )),
    [policies]
  );
  const currentlyWrittenIds = integration.doc?.data.selectedPolicyIds ?? [];
  const onUpdate = useMemo(
    () => async (selected: string[]) => {
      setIsUpdating(true);
      await firestore.updateDoc(integration.ref, {
        selectedPolicyIds: selected,
      } as PagerdutyIntegration);
      setIsUpdating(false);
    },
    [integration.ref]
  );

  /* Refresh on load / install */
  useEffect(() => {
    const cancellation = new Cancellation();
    onRefresh(cancellation);
    return cancellation.cancel;
  }, [onRefresh]);

  const onClickRefresh = useCallback(() => onRefresh(), [onRefresh]);

  return error ? (
    <ErrorDisplay
      title="Could not fetch Pagerduty escalation policies"
      error={error}
    />
  ) : (
    <Space direction="vertical" size="middle">
      <div>
        Select escalation policies for auto-approval:
        <div className="flex">
          {isUpdating || isRefreshing || options === undefined ? (
            <Spin />
          ) : (
            <>
              <Select
                mode="multiple"
                allowClear
                disabled={!canEdit}
                className="grow"
                placeholder="None selected"
                defaultValue={currentlyWrittenIds}
                onChange={onUpdate}
                optionFilterProp="label"
              >
                {options}
              </Select>
              {canEdit && (
                <GraphTooltip title="Fetch latest escalation policies from Pagerduty">
                  <Button onClick={onClickRefresh}>
                    <ReloadOutlined />
                  </Button>
                </GraphTooltip>
              )}
            </>
          )}
        </div>
      </div>
    </Space>
  );
};

export const Pagerduty: React.FC<object> = () => {
  const tenantId = useContext(Tenant);
  const integrationData =
    firestoreProvider.useFirestoreDoc<PagerdutyIntegration>(
      `o/${tenantId}/integrations/pagerduty`,
      { live: true }
    );
  return (
    <OAuth2IntegrationCard
      integration="pagerduty"
      integrationDoc={integrationData.doc}
      mode="pkce"
      logo={PagerdutyIconUrl}
      preamble={
        !integrationData.doc && !integrationData.loading ? (
          <PagerdutyPreamble />
        ) : undefined
      }
    >
      <PagerdutyScheduleSelect integration={integrationData} />
    </OAuth2IntegrationCard>
  );
};
